import { useAuth } from 'auth';
import { environmentVars } from 'constants/services';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import { useObservableState } from 'observable-hooks';
import React, { useMemo, useState } from 'react';
import {
  EMPTY,
  from,
  NEVER,
  Observable,
  of,
  Subject,
  throwError,
  timer,
} from 'rxjs';
import { catchError, delayWhen, mergeMap, retryWhen } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';

const MAX_RETRIES = 5;
const ONE_SECOND_MS = 1000;

export enum EventType {
  FileEvent = 'FileEvent',
  DocumentProcessingEvent = 'DocumentProcessingEvent',
}

export enum SyncJobEventType {
  PendingUpload = 'drive-sync-job-created',
  UploadComplete = 'drive-sync-job-completed',
  UploadError = 'drive-sync-job-error',
}

export enum DriveSourceTargetEventType {
  Added = 'drive-source-target-added',
  Removed = 'drive-source-target-removed',
}

export enum DocumentProcessingEventType {
  MapTilingCompleted = 'map-tiling-completed',
}

type SyncJobPayload = {
  eventTitle: string;
  eventBody: string;
  provider: string;
  syncId?: string;
};

export type DriveSourceTargetPayload = {
  driveId: string;
  ownerId: string;
  target: {
    type: 'file' | 'folder';
    driveId: string;
    itemId: string;
    folderIdPath?: string[];
    siteId?: string;
  };
};

export type FileEventMessageBase = {
  type: EventType.FileEvent;
  userid: string;
  projectid: string;
  driveid: string;
};

export type SyncJobEventMessage = FileEventMessageBase & {
  event:
    | SyncJobEventType.PendingUpload
    | SyncJobEventType.UploadComplete
    | SyncJobEventType.UploadError;
  payload: SyncJobPayload;
};

type DocumentProcessingEventMessage = {
  event: DocumentProcessingEventType;
  eventTitle: string;
  eventBody: string;
  type: EventType.DocumentProcessingEvent;
  projectid: string;
  sheetversionid: string;
};

export type DriveSourceTargetEventMessage = FileEventMessageBase & {
  event: DriveSourceTargetEventType.Added | DriveSourceTargetEventType.Removed;
  payload: DriveSourceTargetPayload;
};

export type FileEventMessage =
  | SyncJobEventMessage
  | DriveSourceTargetEventMessage;

export type DocumentMessage = DocumentProcessingEventMessage;
export type EventMessage = FileEventMessage | DocumentMessage;

export type WebSocketContextValue = {
  receive(): Observable<EventMessage>;
  send<T>(msg: NonNullable<T>): void;
};

type WebSocketContextProviderProps = {
  onReconnect: () => Promise<any>;
  children: React.ReactNode;
};

const EmptyWebSocketContext = {
  receive() {
    return EMPTY;
  },
  send() {},
};

export const WebSocketContext = React.createContext<WebSocketContextValue>(
  EmptyWebSocketContext
);

const generateWebSocket = async (
  accessToken: Bluebeam.Auth.Models.Credentials['accessToken'],
  useAlternateUrls: boolean
): Promise<WebSocketSubject<EventMessage>> => {
  return webSocket(
    `${
      useAlternateUrls
        ? environmentVars.WEBSOCKET_ENDPOINT_02
        : environmentVars.WEBSOCKET_ENDPOINT
    }/conn/${accessToken}`
  );
};

const WebSocketProvider: React.FC<WebSocketContextProviderProps> = ({
  onReconnect,
  children,
}: WebSocketContextProviderProps) => {
  const {
    [FeatureFlag.RealtimeConnection]: isRealtimeConnectionEnabled,
    [FeatureFlag.UseAlternateURLs]: useAlternateUrls,
  } = useFeatureFlags();
  const [lastError, setLastError] = useState<any | undefined>(undefined);
  const { accessToken } = useAuth();

  const wsMemo = useMemo(
    () =>
      of(() => generateWebSocket(accessToken, !!useAlternateUrls)).pipe(
        mergeMap((wsGenerator) => {
          if (!isRealtimeConnectionEnabled) return EMPTY;
          return wsGenerator();
        }),
        delayWhen(() => from(onReconnect())),
        retryWhen((error$: Observable<Error>) =>
          error$.pipe(
            mergeMap((error, i) => {
              const attempt = i + 1;
              if (i < MAX_RETRIES) {
                return timer(ONE_SECOND_MS * attempt);
              }

              return throwError(error);
            })
          )
        )
      ),
    [isRealtimeConnectionEnabled, lastError]
  );

  const ws = useObservableState(wsMemo, new Subject());

  const errorSafeWS = useMemo(() => {
    return ws.pipe(
      catchError((error) => {
        setLastError(error);
        return NEVER;
      })
    );
  }, [ws]);

  const context = useMemo(
    () => ({
      send<T>(message: NonNullable<T>) {
        ws.next(JSON.stringify(message) as unknown as EventMessage);
      },
      receive() {
        return errorSafeWS;
      },
    }),
    [ws]
  );

  return (
    <WebSocketContext.Provider value={context}>
      {children}
    </WebSocketContext.Provider>
  );
};

export { WebSocketProvider };
